import React, { useState, useEffect } from 'react';
import { useLead } from '../../contexts/LeadContext';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiEdit, FiTrash2, FiMessageSquare, FiPhone, FiMail, FiSms, FiPlus, FiDownload } = FiIcons;

const ContactLogModal = ({ lead, onClose, onGenerateContent, onShowReport }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const { fetchContacts, addContact, updateContact, deleteContact } = useLead();
  
  const [formData, setFormData] = useState({
    contact_type: 'phone',
    subject: '',
    content: '',
    notes: ''
  });

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    const result = await fetchContacts(lead.id);
    if (result.success) {
      setContacts(result.data);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const result = await addContact(lead.id, formData);
      if (result.success) {
        setContacts([result.data, ...contacts]);
        setFormData({
          contact_type: 'phone',
          subject: '',
          content: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error saving contact log:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact({ ...contact });
  };

  const handleUpdateContact = async () => {
    try {
      const result = await updateContact(editingContact.id, {
        subject: editingContact.subject,
        content: editingContact.content,
        notes: editingContact.notes
      });
      
      if (result.success) {
        setContacts(contacts.map(c => 
          c.id === editingContact.id ? result.data : c
        ));
        setEditingContact(null);
      }
    } catch (error) {
      console.error('Error updating contact log:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact log entry?')) {
      return;
    }
    
    try {
      const result = await deleteContact(contactId);
      if (result.success) {
        setContacts(contacts.filter(c => c.id !== contactId));
      }
    } catch (error) {
      console.error('Error deleting contact log:', error);
    }
  };

  const getContactTypeIcon = (type) => {
    switch (type) {
      case 'phone': return FiPhone;
      case 'email': return FiMail;
      case 'sms': return FiSms;
      default: return FiMessageSquare;
    }
  };

  const getContactTypeLabel = (type) => {
    switch (type) {
      case 'phone': return 'Phone Call';
      case 'email': return 'Email';
      case 'sms': return 'SMS';
      default: return 'Contact';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Contact Log History</h2>
              <p className="text-gray-600">{lead.name} • {lead.phone}</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onShowReport}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiDownload} className="h-4 w-4" />
                <span>Generate PDF Report</span>
              </button>
              {onGenerateContent && (
                <button
                  onClick={() => onGenerateContent(lead)}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="h-4 w-4" />
                  <span>AI Assistant</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* New Contact Log Form */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Contact Log Entry</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Type
                  </label>
                  <select
                    name="contact_type"
                    value={formData.contact_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  >
                    <option value="phone">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    placeholder="Brief subject or title"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  placeholder="What was discussed or communicated..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  placeholder="Additional notes or follow-up items..."
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-2 bg-electric-600 text-white rounded-lg hover:bg-electric-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiSave} className="h-4 w-4" />
                  <span>{saving ? 'Saving...' : 'Save Contact Log'}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Contact Log History List */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Contact Log History</h3>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-600"></div>
              </div>
            ) : contacts.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-500">No contact log entries yet. Add your first entry above!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map(contact => (
                  <div key={contact.id} className="bg-white border border-gray-200 rounded-lg p-6">
                    {editingContact?.id === contact.id ? (
                      <div className="space-y-4">
                        <input
                          type="text"
                          value={editingContact.subject}
                          onChange={(e) => setEditingContact({
                            ...editingContact,
                            subject: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Subject"
                        />
                        <textarea
                          value={editingContact.content}
                          onChange={(e) => setEditingContact({
                            ...editingContact,
                            content: e.target.value
                          })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <textarea
                          value={editingContact.notes}
                          onChange={(e) => setEditingContact({
                            ...editingContact,
                            notes: e.target.value
                          })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Notes"
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setEditingContact(null)}
                            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdateContact}
                            className="px-3 py-1 bg-electric-600 text-white rounded hover:bg-electric-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <SafeIcon 
                                icon={getContactTypeIcon(contact.contact_type)} 
                                className="h-5 w-5 text-gray-400" 
                              />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-600">
                                  {getContactTypeLabel(contact.contact_type)}
                                </span>
                                <span className="text-sm text-gray-400">•</span>
                                <span className="text-sm text-gray-600">
                                  {format(new Date(contact.contact_date), 'MMM d, yyyy h:mm a')}
                                </span>
                              </div>
                              {contact.subject && (
                                <h4 className="font-medium text-gray-900 mt-1">
                                  {contact.subject}
                                </h4>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditContact(contact)}
                              className="p-1 text-gray-600 hover:text-gray-900"
                            >
                              <SafeIcon icon={FiEdit} className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteContact(contact.id)}
                              className="p-1 text-red-600 hover:text-red-700"
                            >
                              <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="mt-3 text-gray-700 whitespace-pre-wrap">
                          {contact.content}
                        </div>
                        
                        {contact.notes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <strong>Notes:</strong> {contact.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactLogModal;